import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

const sizeColor = (s) => (s >= 2 ? "#7f1d1d" : s >= 1.75 ? "#C8102E" : s >= 1.25 ? "#D97706" : s >= 1 ? "#eab308" : "#22c55e");
const GROUP_COLORS = { A: "#2563eb", B: "#f97316", both: "#7c3aed" };

export default function HailMap({ reports, showHeat, serviceArea, pin, colorMode = "size", severityRings = false, onServiceArea, onCanvassBox }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(null);
  const ringsRef = useRef(null);
  const heatRef = useRef(null);
  const drawnRef = useRef(null);
  const areaRef = useRef(null);
  const pinRef = useRef(null);
  const cbRef = useRef({});
  cbRef.current = { onServiceArea, onCanvassBox };

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const map = L.map(elRef.current, { scrollWheelZoom: false }).setView([29.4241, -98.4936], 6);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    ringsRef.current = L.layerGroup().addTo(map);
    markersRef.current = L.layerGroup().addTo(map);
    drawnRef.current = L.featureGroup().addTo(map);

    const drawControl = new L.Control.Draw({
      position: "topright",
      draw: {
        polygon: { shapeOptions: { color: "#0B2545", weight: 2 } },
        rectangle: { shapeOptions: { color: "#C8102E", weight: 2 } },
        polyline: false, circle: false, marker: false, circlemarker: false,
      },
      edit: { featureGroup: drawnRef.current, edit: false, remove: true },
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (e) => {
      const { layerType, layer } = e;
      if (layerType === "polygon") {
        drawnRef.current.clearLayers();
        drawnRef.current.addLayer(layer);
        const latlngs = layer.getLatLngs()[0].map((p) => [p.lat, p.lng]);
        cbRef.current.onServiceArea && cbRef.current.onServiceArea(latlngs);
      } else if (layerType === "rectangle") {
        drawnRef.current.clearLayers();
        drawnRef.current.addLayer(layer);
        const b = layer.getBounds();
        cbRef.current.onCanvassBox && cbRef.current.onCanvassBox({ south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() });
      }
    });
    map.on(L.Draw.Event.DELETED, () => {
      cbRef.current.onServiceArea && cbRef.current.onServiceArea(null);
      cbRef.current.onCanvassBox && cbRef.current.onCanvassBox(null);
    });

    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 250);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // markers + severity rings + heat
  useEffect(() => {
    const map = mapRef.current, grp = markersRef.current, rings = ringsRef.current;
    if (!map || !grp) return;
    grp.clearLayers();
    rings.clearLayers();
    const pts = [];
    (reports || []).forEach((r) => {
      if (severityRings && r.size_in >= 2) {
        L.circle([r.lat, r.lon], { radius: 4500, color: "#7f1d1d", weight: 1, fillColor: "#b91c1c", fillOpacity: 0.13 }).addTo(rings);
      }
      const isBoth = colorMode === "group" && r.grp === "both";
      const radius = isBoth ? 9 : Math.max(4, Math.min(16, 4 + r.size_in * 5));
      const fill = colorMode === "group" ? (GROUP_COLORS[r.grp] || GROUP_COLORS.A) : sizeColor(r.size_in);
      const m = L.circleMarker([r.lat, r.lon], { radius, color: isBoth ? "#3b0764" : "#fff", weight: isBoth ? 2 : 1, fillColor: fill, fillOpacity: 0.85 });
      const when = (r.valid || "").replace("T", " ").replace(":00Z", " UTC");
      m.bindPopup(`<b>${r.size_in.toFixed(2)}&quot; hail</b>${isBoth ? " <span style='color:#7c3aed;font-weight:bold'>(hit twice)</span>" : ""}<br/>${r.city ? r.city + ", " : ""}${r.county ? r.county + " Co, " : ""}${r.state}<br/><span style="color:#64748b">${when}</span>`);
      m.addTo(grp);
      pts.push([r.lat, r.lon]);
    });
    if (heatRef.current) { map.removeLayer(heatRef.current); heatRef.current = null; }
    if (showHeat && (reports || []).length) {
      const heatPts = reports.map((r) => [r.lat, r.lon, Math.min(1, r.size_in / 3)]);
      heatRef.current = L.heatLayer(heatPts, { radius: 34, blur: 24, maxZoom: 11, minOpacity: 0.35,
        gradient: { 0.2: "#22c55e", 0.45: "#eab308", 0.65: "#D97706", 0.85: "#C8102E", 1: "#7f1d1d" } }).addTo(map);
    }
    if (pts.length && !pin) { try { map.fitBounds(pts, { padding: [30, 30], maxZoom: 10 }); } catch (e) { console.debug("fitBounds skipped:", e?.message || e); } }
  }, [reports, showHeat, pin, colorMode, severityRings]);

  // service area polygon
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (areaRef.current) { map.removeLayer(areaRef.current); areaRef.current = null; }
    if (serviceArea && serviceArea.length >= 3) {
      areaRef.current = L.polygon(serviceArea, { color: "#0B2545", weight: 2, dashArray: "6 6", fillColor: "#0B2545", fillOpacity: 0.06 }).addTo(map);
    }
  }, [serviceArea]);

  // address pin
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pinRef.current) { map.removeLayer(pinRef.current); pinRef.current = null; }
    if (pin && pin.lat != null) {
      pinRef.current = L.marker([pin.lat, pin.lon], {
        icon: L.divIcon({ className: "", html: '<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#0B2545;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>', iconSize: [18, 18], iconAnchor: [9, 18] }),
      }).addTo(map).bindPopup(pin.label || "Property");
      map.setView([pin.lat, pin.lon], 12);
    }
  }, [pin]);

  return <div ref={elRef} data-testid="hail-map" className="w-full h-[460px] rounded-xl overflow-hidden border border-slate-200" style={{ zIndex: 0 }} />;
}
